import { useState } from 'react';
import { useTasks } from '../hooks/useTasks';
import Button from '../components/Button';
import Input from '../components/Input';
import { fmtDT } from '../utils/helpers';
import { TASK_CATEGORIES } from '../utils/constants';
import type { Task } from '../types';

const blank = (): Partial<Task> => ({ title: '', category: '', notes: '' });

function exportTasksCsv(tasks: Task[]) {
  const headers = ['Title', 'Category', 'Status', 'Ticket Ref', 'Notes', 'Date', 'Source'];
  const rows = tasks.map(t => [
    t.title, t.category, t.status ?? '', t.fromTicketId ?? '',
    t.notes ?? '', fmtDT(t.taskDate ?? t.createdAt), t.isAuto ? 'Auto (from ticket)' : 'Manual',
  ]);
  const csv = [headers, ...rows]
    .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), { href: url, download: 'my-tasks.csv' });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Tasks() {
  const { tasks, loading, createTask, deleteTask, completeTask, refetch } = useTasks();
  const [showForm, setShowForm] = useState(false);
  const [form,     setForm]     = useState<Partial<Task>>(blank());
  const [saving,   setSaving]   = useState(false);
  const [working,  setWorking]  = useState<string | null>(null);

  const submit = async () => {
    if (!form.title || !form.category) return;
    setSaving(true);
    try {
      await createTask(form);
      refetch();
      setShowForm(false);
      setForm(blank());
    } finally { setSaving(false); }
  };

  const handleComplete = async (id: string) => {
    setWorking(id);
    try { await completeTask(id); }
    finally { setWorking(null); }
  };

  const active    = tasks.filter(t => t.status !== 'completed');
  const completed = tasks.filter(t => t.status === 'completed');

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-[#010E23]">Tasks</h1>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" disabled={tasks.length === 0} onClick={() => exportTasksCsv(tasks)}>
            ⬇ CSV
          </Button>
          <Button size="sm" onClick={() => setShowForm(v => !v)}>+ Log Task</Button>
        </div>
      </div>

      {/* Manual task form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-3">
          <h2 className="text-sm font-semibold text-[#010E23]">Log New Task</h2>
          <Input
            label="Task Title"
            value={form.title || ''}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            required
          />
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Category *</label>
            <select
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600"
              value={form.category || ''}
              onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
            >
              <option value="">Select</option>
              {TASK_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <textarea
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-violet-600 resize-none"
              rows={2}
              value={form.notes || ''}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" disabled={saving} onClick={submit}>
              {saving ? 'Saving…' : 'Save Task'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 justify-center py-8">
          <div className="w-4 h-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          Loading…
        </div>
      ) : (
        <>
          {/* ── Active tasks ─────────────────────────────────────────────────── */}
          <div>
            <h2 className="text-sm font-semibold text-gray-500 mb-2">
              Active Tasks ({active.length})
            </h2>
            {active.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-4 text-xs text-gray-400 text-center">
                No active tasks
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-gray-50">
                  {active.map(t => (
                    <div key={t.id} className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-medium text-[#010E23]">
                            {t.isAuto && <span className="mr-1 text-violet-500">🔗</span>}
                            {t.title}
                            {t.status === 'in_progress' && (
                              <span className="ml-1.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold">In Progress</span>
                            )}
                          </p>
                          <p className="text-[11px] text-gray-400 mt-0.5">{t.category} · {fmtDT(t.taskDate ?? t.createdAt)}</p>
                          {t.fromTicketId && <p className="font-mono text-[10px] text-violet-700">{t.fromTicketId}</p>}
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          {t.isAuto ? (
                            <span className="text-[10px] text-gray-400 italic">Completes on SR Closed</span>
                          ) : (
                            <>
                              <Button size="sm" variant="success" disabled={working === t.id} onClick={() => handleComplete(t.id)}>
                                {working === t.id ? '…' : 'Complete'}
                              </Button>
                              <button onClick={() => deleteTask(t.id)} className="text-xs text-red-500 hover:underline">Delete</button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">Title</th>
                        <th className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">Category</th>
                        <th className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">Ticket Ref</th>
                        <th className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">Date</th>
                        <th className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {active.map(t => (
                        <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="px-4 py-3 text-xs font-medium text-[#010E23]">
                            {t.isAuto && <span className="mr-1.5 text-violet-500" title="From ticket">🔗</span>}
                            {t.title}
                            {t.status === 'in_progress' && (
                              <span className="ml-1.5 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold">In Progress</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">{t.category}</td>
                          <td className="px-4 py-3 font-mono text-[10px] text-violet-700">{t.fromTicketId || '—'}</td>
                          <td className="px-4 py-3 text-xs text-gray-400">{fmtDT(t.taskDate ?? t.createdAt)}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              {t.isAuto ? (
                                <span className="text-[10px] text-gray-400 italic">Completes on SR Closed</span>
                              ) : (
                                <>
                                  <Button size="sm" variant="success" disabled={working === t.id} onClick={() => handleComplete(t.id)}>
                                    {working === t.id ? '…' : 'Mark as Complete'}
                                  </Button>
                                  <button onClick={() => deleteTask(t.id)} className="text-xs text-red-500 hover:underline">Delete</button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* ── Completed tasks ───────────────────────────────────────────────── */}
          <div>
            <h2 className="text-sm font-semibold text-gray-500 mb-2">
              Completed Tasks ({completed.length})
            </h2>
            {completed.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 p-4 text-xs text-gray-400 text-center">
                No completed tasks yet
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-gray-50">
                  {completed.map(t => {
                    const notesLower = (t.notes ?? '').toLowerCase();
                    const isResolved    = t.isAuto && notesLower.startsWith('resolved');
                    const isNotResolved = t.isAuto && notesLower.startsWith('not resolved');
                    return (
                      <div key={t.id} className="p-4 opacity-80">
                        <p className="text-xs font-medium text-[#010E23] mb-1">
                          {t.isAuto && <span className="mr-1 text-violet-500">🔗</span>}{t.title}
                        </p>
                        <p className="text-[11px] text-gray-400 mb-2">{t.category} · {fmtDT(t.updatedAt ?? t.createdAt)}</p>
                        <div className="flex items-center justify-between">
                          {t.fromTicketId && <span className="font-mono text-[10px] text-violet-700">{t.fromTicketId}</span>}
                          {isResolved ? (
                            <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Resolved ✓</span>
                          ) : isNotResolved ? (
                            <span className="text-[10px] font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Not Resolved ✗</span>
                          ) : (
                            <span className="text-[10px] font-semibold bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Completed</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">Title</th>
                        <th className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">Category</th>
                        <th className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">Ticket Ref</th>
                        <th className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">Outcome</th>
                        <th className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completed.map(t => {
                        const notesLower = (t.notes ?? '').toLowerCase();
                        const isResolved    = t.isAuto && notesLower.startsWith('resolved');
                        const isNotResolved = t.isAuto && notesLower.startsWith('not resolved');
                        return (
                          <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50 opacity-80">
                            <td className="px-4 py-3 text-xs font-medium text-[#010E23]">
                              {t.isAuto && <span className="mr-1.5 text-violet-500">🔗</span>}{t.title}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">{t.category}</td>
                            <td className="px-4 py-3 font-mono text-[10px] text-violet-700">{t.fromTicketId || '—'}</td>
                            <td className="px-4 py-3">
                              {isResolved ? (
                                <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full whitespace-nowrap">Resolved ✓</span>
                              ) : isNotResolved ? (
                                <span className="text-[10px] font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded-full whitespace-nowrap">Not Resolved ✗</span>
                              ) : (
                                <span className="text-[10px] font-semibold bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Completed</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-400">{fmtDT(t.updatedAt ?? t.createdAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
